"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Car } from "../page";
import { shuffle } from "@/utility";
import Image, { StaticImageData } from "next/image";
import { useTranslations } from "next-intl";
import ModalAsButton from "@/app/components/ModalAsButton";

type Props = {
  allCars: Car[];
  title: string;
  description: React.ReactNode;
  more: React.ReactNode;
};

export default function FullCareer({ allCars, title, description, more }: Props) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

  const shuffleCars = useCallback(() => {
    return shuffle(allCars).slice(0, isMobile ? 6 : 14);
  }, [allCars, isMobile]);

  const [cars, setCars] = useState(shuffleCars());
  const [lastCars, setLastCars] = useState<Car[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const imageRef = useRef<{image: StaticImageData, name: string} | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const zoomCar = (name: string) => {
    imageRef.current = allCars.find((car) => car.name === name) || null;
    toggleModal();
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setupInterval = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = window.setInterval(() => {
      setShuffledCars();
    }, 10000 + cars.length * 500);
  };

  useEffect(() => {
    setupInterval();
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [setupInterval]);

  const setShuffledCars = useCallback(() => {
    setIsLoading(true);

    let newCars = shuffleCars();
    while (newCars.some((car) => lastCars.some((c) => c.name === car.name) || cars.some((c) => c.name === car.name))) {
      newCars = shuffleCars();
    }

    setLastCars([...cars]);
    setCars(newCars);
    setupInterval();
    setTimeout(() => setIsLoading(false), 1000);
  }, [cars, lastCars, setupInterval, shuffleCars]);

  const t = useTranslations("common");

  return (
    <>
      <div className="max-lg:text-center max-lg:flex max-lg:justify-center max-lg:mb-4 lg:grid grid-cols-2 gap-4">
        <div className="max-w-prose flex flex-col gap-2">
          <h2 className="text-4xl font-semibold title-white max-lg:text-center">
            {title}
          </h2>
          <div className="text-white text-opacity-75">
            {description}{' '}
            <ModalAsButton id={'fullCareerModal'} button={t('findOutMore')}>
              {more}
            </ModalAsButton>
            <div className="sr-only">{more}</div>
          </div>
        </div>
        <div className="max-lg:hidden self-end">
          <div className="flex flex-row justify-end mb-6">
            <button
              className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-75 opacity-100 transition-opacity ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={setShuffledCars}
            >
              {t("more")} {t("cars")}
            </button>
          </div>
          <div className="w-full grid grid-cols-2 gap-4">
            {cars.slice(8, 14).map((group, i) => (
              <div
                key={i}
                className={
                  "cursor-pointer bg-gray-400 bg-opacity-5 rounded-lg h-24 w-full p-4 flex justify-center items-center hover:opacity-75 " +
                  (isLoading && "animate-bounce")
                }
                onClick={() => zoomCar(group.name)}
              >
                <Image
                  src={group.image}
                  alt={group.name}
                  className={
                    "max-h-full w-auto h-fit " +
                    (group.orientation === "left"
                      ? "transform scale-x-[-1]"
                      : "") +
                    " " +
                    (isLoading && "hidden")
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        className={
          "w-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-4 pb-6"
        }
      >
        {cars.slice(0, isMobile ? 6 : 8).map((group, i) => (
          <div
            key={i}
            className={
              "cursor-pointer bg-gray-400 bg-opacity-5 rounded-lg h-24 w-full p-4 flex justify-center items-center hover:opacity-75 " +
              (isLoading && "animate-bounce")
            }
            onClick={() => zoomCar(group.name)}
          >
            <Image
              src={group.image}
              alt={group.name}
              className={
                "max-h-full w-auto h-fit " +
                (group.orientation === "left" ? "transform scale-x-[-1]" : "") +
                " " +
                (isLoading && "hidden")
              }
            />
          </div>
        ))}
      </div>
      <p className="text-center text-creme">{t("allCarsAvailable")}</p>
      {modalIsOpen && (
        <div className="text-main text-left h-fit max-h-[75%] max-md:min-w-[75%] max-w-prose overflow-y-auto fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white bg-opacity-25 backdrop-blur-lg pt-4 z-[1000] rounded-xl overflow-hidden">
          {imageRef.current && (
            <div className="flex flex-col gap-y-4 justify-center">
            <Image src={imageRef.current.image} alt="zoomed car" className="px-4" />
            <div className="text-center text-black drop-shadow-md text-2xl py-2 bg-creme bg-opacity-75">{imageRef.current.name}</div>
          </div>
          )}
        </div>
      )}
      {modalIsOpen && (
        <div
          className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-[999]"
          onClick={toggleModal}
        ></div>
      )}
    </>
  );
}
